import React from 'react';

interface OrbaryxLogoProps {
  className?: string;
  size?: number;
  showText?: boolean;
}

export function OrbaryxLogo({ className = '', size = 56, showText = false }: OrbaryxLogoProps) {
  const id = React.useId().replace(/:/g, '');
  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Main sphere gradient – cyan top-left → indigo → deep purple */}
          <radialGradient id={`sg-${id}`} cx="38%" cy="28%" r="72%">
            <stop offset="0%"   stopColor="#22D3EE" />
            <stop offset="35%"  stopColor="#6366F1" />
            <stop offset="100%" stopColor="#6B21A8" />
          </radialGradient>

          {/* Subtle highlight at top of sphere */}
          <radialGradient id={`hl-${id}`} cx="40%" cy="20%" r="50%">
            <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </radialGradient>

          {/* Clip to circle */}
          <clipPath id={`cc-${id}`}>
            <circle cx="100" cy="100" r="95" />
          </clipPath>

          {/* Soft glow on X arms */}
          <filter id={`gf-${id}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ── Sphere base ── */}
        <circle cx="100" cy="100" r="95" fill={`url(#sg-${id})`} />
        <circle cx="100" cy="100" r="95" fill={`url(#hl-${id})`} />

        {/* ── X pattern: 4 curved blade panels ── */}
        <g clipPath={`url(#cc-${id})`} filter={`url(#gf-${id})`}>
          {/*
            Each blade is a quadrilateral from the center area out to the rim.
            Thin dark "cracks" between blades let the sphere colour show through.
            Blades are clipped by a slightly smaller central diamond so there's a
            clean gap at the exact centre.
          */}

          {/* Top-left blade */}
          <path
            d="M100 100 L14 14 C6 34 6 66 20 82 L100 100 Z"
            fill="white" fillOpacity="0.88"
          />
          {/* Top-right blade */}
          <path
            d="M100 100 L186 14 C194 34 194 66 180 82 L100 100 Z"
            fill="white" fillOpacity="0.82"
          />
          {/* Bottom-left blade */}
          <path
            d="M100 100 L14 186 C6 166 6 134 20 118 L100 100 Z"
            fill="white" fillOpacity="0.78"
          />
          {/* Bottom-right blade */}
          <path
            d="M100 100 L186 186 C194 166 194 134 180 118 L100 100 Z"
            fill="white" fillOpacity="0.72"
          />

          {/* ── Dark separators (the "cracks" between blades) ── */}
          {/* Top separator – from centre up */}
          <line x1="100" y1="100" x2="100" y2="5"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.6" />
          {/* Bottom separator */}
          <line x1="100" y1="100" x2="100" y2="195"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.6" />
          {/* Left separator */}
          <line x1="100" y1="100" x2="5" y2="100"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.6" />
          {/* Right separator */}
          <line x1="100" y1="100" x2="195" y2="100"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.6" />

          {/* ── Diagonal separators (the actual X dividing lines) ── */}
          <line x1="100" y1="100" x2="14" y2="14"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.55" />
          <line x1="100" y1="100" x2="186" y2="14"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.55" />
          <line x1="100" y1="100" x2="14" y2="186"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.55" />
          <line x1="100" y1="100" x2="186" y2="186"
            stroke="#1E1B4B" strokeWidth="3.5" strokeOpacity="0.55" />

          {/* ── Centre bright node ── */}
          <circle cx="100" cy="100" r="7" fill="white" fillOpacity="0.95" />
          <circle cx="100" cy="100" r="4" fill="white" />
        </g>

        {/* ── Outer rim / glow ring ── */}
        <circle cx="100" cy="100" r="94"
          stroke="url(#sg-${id})" strokeWidth="0" fill="none" />
        <circle cx="100" cy="100" r="93"
          stroke="white" strokeWidth="1.5" strokeOpacity="0.12" fill="none" />
      </svg>

      {showText && (
        <div className="mt-3 text-center">
          <div className="text-white font-bold text-xl tracking-wider">Orbaryx</div>
          <div className="text-white/45 text-[9px] tracking-[0.25em] uppercase mt-0.5">
            IT Runs Itself
          </div>
        </div>
      )}
    </div>
  );
}
